import { useCallback, useEffect, useRef, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { GLOBAL_PRIORITY_SEED } from '@/services/allocationPriority/consts'
import { cloneHour, cloneHours, createInitialSnapshot } from '@/services/allocationPriority/snapshot'
import type { AllocationPriorityApi, CapacitySnapshot, HourPriority, PrioritySnapshot } from '@/services/allocationPriority/types'
import { dateKey, isHourLocked, operatingDate, validateHour, validatePriority } from '@/services/allocationPriority/validation'
import { POLICY_ERROR_MESSAGE } from '@/services/allocationPriority/errors'
import { capacityOf } from '@/services/allocationPriority/realApi'
import type { ActionCtx, FormState, Wiring } from './handoff/page.generated'
import type { DispatchState } from './dispatchShared'
import type { CapaNow } from './dispatchParts'
import { CAPA_UPDATED_EMPTY, formatCapaUpdatedLabel } from './capaUpdatedLabel'
import { createHourFormSchema, createScenarioFormSchema, exceptionScopeHours, requiredForm, toPolicyOrder, validateTargetOrder, validateWait } from './schemas/priorityForm'

// 성공 뒤 비울 기본 모달 초안 필드. (시간 예외 모달 [초기화]는 서버를 호출하지 않아 perform 을 쓰지 않는다 — LOGISPRD-15037)
const BASE_DRAFT_RESET: FormState = { hourOrder: undefined, hourWait: undefined, hourRanked: undefined }
// 운영 [전체 초기화] 성공 뒤 비울 운영 모달 초안. 저장 시간대도 비워 다음 저장이 초기화 전 값을 되살리지 않게 한다.
const SCEN_DRAFT_RESET: FormState = { hourDrafts: {}, savedHours: {} }

const sameOrder = (a: number[], b: number[]): boolean => a.length === b.length && a.every((v, i) => v === b[i])
/** LOGISPRD-14424: 순위 비교는 order 와 순위 구간 길이(ranked)를 함께 본다. targets 없는 경로(테스트 픽스처)는 둘 다 undefined 라 종전과 같다. */
const sameRank = (order: number[], ranked: number | undefined, other: HourPriority): boolean =>
  sameOrder(order, other.order) && ranked === other.ranked
const rankedOf = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined)
/** ranked 가 있을 때만 키를 싣는다(targets 없는 경로의 값 모양 무변경). */
const withRanked = <T extends object>(value: T, ranked: number | undefined): T & { ranked?: number } =>
  ranked === undefined ? value : { ...value, ranked }
const selectedDate = (today: Date, offset: number): string => {
  const date = new Date(today)
  date.setDate(date.getDate() + offset)
  return dateKey(date)
}
const effectiveHour = (state: PrioritySnapshot, date: string, hr: number, includeException = true): HourPriority => {
  const scenario = state.scenarios.find((s) => s.from <= date && date <= s.to)
  return cloneHour((includeException && state.exceptions[`${date}|${hr}`]) || scenario?.hours[hr] || state.base[hr])
}
/** 생성본의 고정 19행 표시를 위한 어댑터. 마지막 6개는 미지원 매장 표시 전용이다.
 * 사용자 승인에 따라 저장 시 검증된 19개 순열만 센터 상대 순서로 투영한다. */
const displayOrder = (order: number[]): number[] => [...order, 13, 14, 15, 16, 17, 18]
/** LOGISPRD-14424: 실 모드(snapshot.targets 있음)는 서버 창고 순서를 그대로 보여 준다(매장 고정 행 없음). */
const viewOrder = (snapshot: PrioritySnapshot, order: number[]): number[] => (snapshot.targets ? [...order] : displayOrder(order))
const targetCountOf = (snapshot: PrioritySnapshot): number | undefined => snapshot.targets?.length
/** 렌더 경로용 toPolicyOrder — 형식이 맞지 않으면 던지지 않고 undefined. */
const safePolicyOrder = (order: unknown, targetCount: number | undefined): number[] | undefined => {
  try {
    return toPolicyOrder(order as number[], targetCount)
  } catch {
    return undefined
  }
}
const rankedMap = <K extends string | number>(entries: [K, HourPriority][]): Record<K, number> =>
  Object.fromEntries(entries.flatMap(([key, value]) => (value.ranked === undefined ? [] : [[key, value.ranked]]))) as Record<K, number>
const toDispatch = (snapshot: PrioritySnapshot): DispatchState => ({
  ...(snapshot.targets
    ? {
        basePrioRanked: rankedMap(Object.entries(snapshot.base).map(([hr, value]) => [Number(hr), value] as [number, HourPriority])),
        scenPrioRanked: Object.fromEntries(
          snapshot.scenarios.map((s) => [s.id, rankedMap(Object.entries(s.hours).map(([hr, value]) => [Number(hr), value] as [number, HourPriority]))]),
        ),
        prioEditsRanked: rankedMap(Object.entries(snapshot.exceptions)),
      }
    : {}),
  scenarios: snapshot.scenarios.map((s, i) => ({
    id: s.id,
    seq: i + 1,
    name: s.name,
    full: `${s.name} 운영`,
    desc: '운영',
    color: 'c1',
    from: s.from,
    to: s.to,
  })),
  basePrio: Object.fromEntries(Object.entries(snapshot.base).map(([hr, value]) => [hr, viewOrder(snapshot, value.order)])),
  baseWait: Object.fromEntries(Object.entries(snapshot.base).map(([hr, value]) => [hr, value.wait])),
  scenPrio: Object.fromEntries(
    snapshot.scenarios.map((s) => [s.id, Object.fromEntries(Object.entries(s.hours).map(([hr, value]) => [hr, viewOrder(snapshot, value.order)]))]),
  ),
  scenWait: Object.fromEntries(
    snapshot.scenarios.map((s) => [s.id, Object.fromEntries(Object.entries(s.hours).map(([hr, value]) => [hr, value.wait]))]),
  ),
  prioEdits: Object.fromEntries(Object.entries(snapshot.exceptions).map(([key, value]) => [key, viewOrder(snapshot, value.order)])),
  waitEdits: Object.fromEntries(Object.entries(snapshot.exceptions).map(([key, value]) => [key, value.wait])),
  globalPrio: [...GLOBAL_PRIORITY_SEED],
  scenVals: {},
  scenDow: {},
  todayExc: {},
  cvBase: {},
  dowExc: {},
})

/**
 * LOGISPRD-14424 실 모드: 폼 순위 초안이 현재 targets 길이의 순열이 아니면(첫 조회 전 19행 초안·창고 수 변경) 쓰지 않는다.
 * 초안이 없으면 true(ranked 만 바꾼 편집 허용). targets 없는 경로(테스트 픽스처)는 항상 true — 기존 13/19 경로 그대로.
 */
const usableDraftOrder = (snapshot: PrioritySnapshot, order: unknown): boolean =>
  !snapshot.targets || order === undefined || safePolicyOrder(order, snapshot.targets.length) !== undefined

export interface AllocationPriorityWiringResult {
  wiring: Wiring
  error: string
  loading: boolean
  ready: boolean
  /** LOGISPRD-14424 REQ-19: CAPA 조회 실패 문구. 순위 화면(error)과 분리한다 — 화면을 막지 않는다. */
  capaError: string
}

export function useAllocationPriorityWiring(api: AllocationPriorityApi): AllocationPriorityWiringResult {
  const [snapshot, setSnapshot] = useState(createInitialSnapshot)
  // 편집 중인 날짜를 시간 경과로 재지정하지 않는다. [오늘] 또는 재진입 시 기준일을 갱신한다.
  const [today, setToday] = useState(() => operatingDate(api.now()))
  const [dayOffset, setDayOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const requestId = useRef(0)
  const writing = useRef(false)
  const mounted = useRef(false)
  // 기본 운영 "조정" 판정 기준 — api 가 기준을 주면(테스트 픽스처 시드) 그것을, 아니면(실 API) 진입 후 첫 조회 성공 값을 쓴다.
  // 실 API 에는 기본값 개념이 없어 "이번 진입 이후 바뀐 시간"을 뜻하며, 재진입하면 표시가 초기화된다.
  const initialBase = useRef<PrioritySnapshot['base'] | undefined>(undefined)
  const reportError = useCallback((reason: unknown): Error => {
    const failure = reason instanceof Error ? reason : new Error('처리에 실패했습니다.')
    if (mounted.current) {
      setError(failure.message)
      core.message.error(failure.message)
    }
    return failure
  }, [])
  const refresh = useCallback(async (): Promise<void> => {
    const id = ++requestId.current
    if (mounted.current) setLoading(true)
    try {
      const data = await api.read()
      if (mounted.current && id === requestId.current) {
        initialBase.current ??= cloneHours(data.base)
        setSnapshot(data)
        setReady(true)
        setError('')
      }
    } catch (reason) {
      if (mounted.current && id === requestId.current) throw reportError(reason)
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false)
    }
  }, [api, reportError])
  useEffect(() => {
    mounted.current = true
    refresh().catch(() => undefined)
    return () => {
      mounted.current = false
      requestId.current++
    }
  }, [refresh, dayOffset, today])

  // LOGISPRD-14424 REQ-17: 실 모드 CAPA — 화면 선택 운영일 기준. 날짜가 바뀌면 다시 조회하고 늦게 온 응답은 버린다.
  // api.capacity 없음(테스트 픽스처)은 조회하지 않고 데모 CAPA 를 쓴다. 실패는 데모 값으로 대체하지 않고 재시도하지 않는다.
  const [capacity, setCapacity] = useState<CapacitySnapshot | null>(null)
  const [capaError, setCapaError] = useState('')
  const capaRequest = useRef(0)
  // 새로고침 액션마다 1 증가 → 현재 capaDate 로 한 번 다시 조회한다(자동 재시도 없음).
  const [capaReload, setCapaReload] = useState(0)
  const capaDate = selectedDate(today, dayOffset)
  useEffect(() => {
    const id = ++capaRequest.current
    setCapacity(null)
    setCapaError('')
    if (!api.capacity) return undefined
    api.capacity(capaDate).then(
      (data) => {
        if (id === capaRequest.current && data.operationDate === capaDate) setCapacity(data)
      },
      (reason: unknown) => {
        if (id === capaRequest.current) setCapaError(reason instanceof Error ? reason.message : POLICY_ERROR_MESSAGE.GENERIC)
      },
    )
    return () => {
      capaRequest.current++
    }
  }, [api, capaDate, capaReload])

  const perform =
    (operation: (form: FormState, ctx: ActionCtx) => Promise<unknown>, draftReset?: FormState) =>
    async (ctx: ActionCtx): Promise<string | false> => {
      if (ctx.isCurrent?.() === false) return false
      try {
        const form = requiredForm(ctx.form)
        if (!ready || loading) throw new Error('조회 완료 후 다시 시도하세요.')
        if (writing.current) throw new Error('저장이 진행 중입니다.')
        writing.current = true
        // 새 저장은 이전 조회를 무효화한다. 날짜 이동도 동일한 번호로 응답을 구분한다.
        requestId.current++
        try {
          await operation(form, ctx)
          try {
            await refresh()
          } catch {
            throw new Error('저장은 반영되었지만 재조회에 실패했습니다. 새로고침으로 확인하세요.')
          }
          if (draftReset) ctx.applyDraft?.(draftReset)
          return '저장했습니다.'
        } finally {
          writing.current = false
        }
      } catch (reason) {
        throw reportError(reason)
      }
    }
  const hourTarget = (form: FormState): number => {
    if (typeof form.hr !== 'number') throw new Error('생성본 UI에서 시간대가 연결되지 않아 저장할 수 없습니다.')
    validateHour(form.hr)
    return form.hr
  }
  const idTarget = (form: FormState): string => {
    if (typeof form.id !== 'string' || !form.id) throw new Error('생성본 UI에서 운영 대상이 연결되지 않았습니다.')
    return form.id
  }
  const exceptionTarget = (form: FormState): { date: string; hr: number } => {
    const hr = hourTarget(form)
    if (typeof form.dayOffset !== 'number' || !Number.isInteger(form.dayOffset)) throw new Error('운영 날짜가 연결되지 않았습니다.')
    return { date: selectedDate(today, form.dayOffset), hr }
  }
  const deriveHour = (form: FormState): FormState => {
    if (typeof form.hr !== 'number' || !Number.isInteger(form.hr) || form.hr < 0 || form.hr > 23 || typeof form.dayOffset !== 'number')
      return { ...form, changed: false, locked: true }
    const date = selectedDate(today, form.dayOffset)
    const original = effectiveHour(snapshot, date, form.hr)
    const base = effectiveHour(snapshot, date, form.hr, false)
    // 실 모드에서 targets 길이와 맞지 않는 순위 초안은 렌더 중 던지지 않고 무시한다(order·ranked 함께 유효값).
    const useDraft = usableDraftOrder(snapshot, form.order)
    const order = useDraft && Array.isArray(form.order) ? [...(form.order as number[])] : viewOrder(snapshot, original.order)
    const wait = typeof form.wait === 'number' ? form.wait : original.wait
    const ranked = (useDraft ? rankedOf(form.ranked) : undefined) ?? original.ranked
    let policyOrder: number[]
    try {
      policyOrder = toPolicyOrder(order, targetCountOf(snapshot))
    } catch {
      return withRanked({ ...form, order, wait, changed: false, locked: true }, ranked)
    }
    const locked = !ready || loading || isHourLocked(date, form.hr, api.now())
    const prioIsBase = sameRank(policyOrder, ranked, base)
    return withRanked(
      {
        ...form,
        order,
        wait,
        locked,
        // LOGISPRD-14596: 대상 시간(one → 선택 시간, after → 22시까지) 중 하나라도 유효값과 다르면 변경이다.
        changed:
          !locked &&
          exceptionScopeHours(form.hr, form.scope === 'after' ? 'after' : 'one').some((hr) => {
            const target = effectiveHour(snapshot, date, hr)
            return !sameRank(policyOrder, ranked, target) || wait !== target.wait
          }),
        prioIsBase,
        waitIsBase: wait === base.wait,
        // LOGISPRD-15037: 이름과 달리 "[초기화] 비활성" 판정이다 — after 범위는 초기화 대상이 아니라 항상 true.
        allIsBase: form.scope === 'after' || (prioIsBase && wait === base.wait),
      },
      ranked,
    )
  }
  const deriveSettings = (form: FormState, scenario = false): FormState => {
    const hr = typeof form.hr === 'number' && Number.isInteger(form.hr) && form.hr >= 0 && form.hr <= 23 ? form.hr : undefined
    const value =
      hr === undefined ? undefined : (scenario ? snapshot.scenarios.find((s) => s.id === form.id)?.hours[hr] : undefined) ?? snapshot.base[hr]
    // 실 모드에서 targets 길이와 맞지 않는 순위 초안은 렌더 중 던지지 않고 무시한다(hourOrder·hourRanked 함께 서버값).
    const useDraft = usableDraftOrder(snapshot, form.hourOrder)
    const hourOrder =
      useDraft && Array.isArray(form.hourOrder) ? [...(form.hourOrder as number[])] : value ? viewOrder(snapshot, value.order) : undefined
    const hourWait = form.hourWait ?? value?.wait
    // LOGISPRD-14424: 순위 구간 길이 — 편집값이 없으면 서버값. 리셋(hourRanked: undefined) 후에도 서버값으로 돌아온다.
    const hourRanked = (useDraft ? rankedOf(form.hourRanked) : undefined) ?? value?.ranked
    const baseline = api.baseline?.() ?? initialBase.current ?? snapshot.base
    const modifiedHours = scenario
      ? []
      : Object.entries(snapshot.base).flatMap(([hour, saved]) =>
          !sameRank(saved.order, saved.ranked, baseline[Number(hour)]) || saved.wait !== baseline[Number(hour)].wait ? [Number(hour)] : [],
        )
    let hourDirty = false
    let hourModified = false
    if (hr !== undefined && value && hourOrder) {
      try {
        const policy = toPolicyOrder(hourOrder, targetCountOf(snapshot))
        hourDirty = !sameRank(policy, hourRanked, value) || hourWait !== value.wait
        const initial = baseline[hr]
        hourModified =
          !sameRank(policy, hourRanked, initial) ||
          hourWait !== initial.wait ||
          !sameRank(value.order, value.ranked, initial) ||
          value.wait !== initial.wait
      } catch {
        hourDirty = true
      }
    }
    if (!scenario && hr !== undefined && hourModified && !modifiedHours.includes(hr)) modifiedHours.push(hr)
    return {
      ...form,
      ...(!scenario ? { modifiedHours } : {}),
      // 컨덕터 데모 기준(LUA 2026-10-14): 바꾼 게 없으면 [기본 우선순위 저장] 비활성. 저장은 선택한 국내 시간대 한 칸이라 그 칸의 변경만 본다.
      ...(!scenario ? { dirty: form.tab !== 'global' && hourDirty } : {}),
      // [전체 초기화]는 저장된 정책을 초기값으로 되돌리는 버튼 — 되돌릴 것(조정된 시간·편집 중 초안)이 없을 때만 끈다.
      ...(!scenario ? { resetAllDisabled: form.tab === 'global' || (modifiedHours.length === 0 && !hourDirty) } : {}),
      ...(hourRanked !== undefined ? { hourRanked } : {}),
      hourEmpty: hr === undefined,
      hourWait,
      hourOrder,
      hourDirty,
      hourModified,
      hourWaitEdited: value && hourWait !== value.wait,
      hourTitle: hr === undefined ? '' : `${String(hr).padStart(2, '0')}:00 ~ ${String((hr + 1) % 24).padStart(2, '0')}:00`,
    }
  }

  // null is an explicit inheritance reset, including removal of a saved/source override.
  const deriveScenario = (form: FormState): FormState => {
    const sourceId = form.loadFrom === undefined ? form.id : form.loadFrom
    const source = snapshot.scenarios.find((s) => s.id === sourceId)
    const sourceHours = (sourceId === form.id ? form.savedHours as Record<number, HourPriority> | undefined : undefined) ?? source?.hours
    const targetCount = targetCountOf(snapshot)
    const existing = snapshot.scenarios.find((s) => s.id === form.id)
    const drafts = (form.hourDrafts ?? {}) as Record<number, HourPriority | null>
    const hr = typeof form.hr === 'number' && Number.isInteger(form.hr) && form.hr >= 0 && form.hr <= 23 ? form.hr : undefined
    // 실 모드만 렌더 중 던지지 않고 길이가 targets 와 다른 순위(옛 19행·이전 창고 집합)를 무시한다.
    // targetCount 없는 경로(테스트 픽스처)는 종전대로 projectCenterOrder 가 형식 오류를 던진다.
    const orderOf = (order: unknown): number[] | undefined =>
      targetCount === undefined ? toPolicyOrder(order as number[]) : safePolicyOrder(order, targetCount)
    const valueAt = (hour: number): HourPriority => {
      const saved = sourceHours?.[hour]
      // 대체값: 불러온/편집 중 운영의 서버 값 → 그 운영에 해당 시간이 없을 때만 기본 운영 값.
      const server = source?.hours[hour] ?? snapshot.base[hour]
      const fallback = saved && orderOf(saved.order) ? saved : server
      if (!Object.prototype.hasOwnProperty.call(drafts, hour)) return fallback
      const draft = drafts[hour]
      if (!draft) return snapshot.base[hour]
      const order = orderOf(draft.order)
      if (!order) return fallback
      // LOGISPRD-14424: 초안의 ranked 가 우선, 없으면 원본(불러온 운영/저장본 → 기본)의 ranked.
      return withRanked({ order, wait: draft.wait }, rankedOf(draft.ranked) ?? fallback.ranked)
    }
    const modifiedHours = Object.keys(snapshot.base).map(Number).filter((hour) => {
      const value = valueAt(hour)
      return value.wait !== snapshot.base[hour].wait || !sameRank(value.order, value.ranked, snapshot.base[hour])
    })
    const value = hr === undefined ? undefined : valueAt(hr)
    // 컨덕터 데모 기준(LUA 2026-10-13): 창을 연 시점과 달라진 게 없으면 [운영 저장] 비활성.
    //  연 시점 = 운영명·기간(수정이면 저장본, 추가면 빈 값) · 시간별 값(초안 없이 보이던 값). 다른 운영 불러오기도 변경이다.
    const openValueAt = (hour: number): HourPriority => {
      const saved = sourceHours?.[hour]
      const server = source?.hours[hour] ?? snapshot.base[hour]
      return saved && orderOf(saved.order) ? saved : server
    }
    const hourChanged = (hour: number): boolean => {
      try {
        const now = valueAt(hour)
        const open = openValueAt(hour)
        return now.wait !== open.wait || !sameRank(toPolicyOrder(now.order, targetCount), now.ranked, { ...open, order: toPolicyOrder(open.order, targetCount) })
      } catch {
        return true
      }
    }
    const openPeriod = existing ? [existing.from, existing.to] : []
    const ymdOf = (d: unknown): string =>
      typeof d === 'string' ? d : d && typeof (d as { format?: unknown }).format === 'function' ? (d as { format: (f: string) => string }).format('YYYY-MM-DD') : ''
    const period = Array.isArray(form.period) ? (form.period as unknown[]).map(ymdOf) : openPeriod
    const dirty =
      (form.name ?? existing?.name ?? '') !== (existing?.name ?? '') ||
      period.length !== openPeriod.length ||
      period.some((d, i) => d !== openPeriod[i]) ||
      (form.loadFrom !== undefined && form.loadFrom !== form.id) ||
      Object.keys(drafts).some((hour) => hourChanged(Number(hour)))
    return {
      ...form,
      dirty,
      resetAllDisabled: modifiedHours.length === 0,
      name: form.name ?? existing?.name ?? '',
      period: form.period === undefined && existing ? [existing.from, existing.to] : form.period,
      modifiedHours,
      hourEmpty: hr === undefined,
      hourOrder: value ? viewOrder(snapshot, value.order) : undefined,
      hourWait: value?.wait,
      ...(value?.ranked !== undefined ? { hourRanked: value.ranked } : {}),
      hourModified: hr !== undefined && modifiedHours.includes(hr),
      hourWaitEdited: hr !== undefined && value?.wait !== snapshot.base[hr].wait,
      hourTitle: hr === undefined ? '' : `${String(hr).padStart(2, '0')}:00 ~ ${String((hr + 1) % 24).padStart(2, '0')}:00`,
    }
  }

  /** LOGISPRD-14424 REQ-18: 실 모드 행별 CAPA. 선택 운영일의 응답만 쓰고, 표시할 값이 없으면 null(표시 없음). */
  const capaOf = (ci: number): CapaNow | null => {
    const code = snapshot.targets?.[ci]?.code
    if (!code || !capacity || capacity.operationDate !== capaDate) return null
    const value = capacityOf(capacity, code)
    if (!value) return null
    // LOGISPRD-14579 R2~R4: used / target · 사용률. 100% 초과는 자르지 않고, 분모가 0/없으면 % 를 만들지 않는다.
    const { used } = value
    // R4: UNLIMITED 에서 target 0 이하는 target 없음으로 취급한다(used / ∞). LIMITED target 0 은 used / 0 그대로.
    const target = value.unlimited && value.target !== null && value.target <= 0 ? null : value.target
    // UNLIMITED 는 값이 없어도 ∞ 를 표시한다(mode 는 실데이터). 둘 다 없음 → 표시 없음은 LIMITED 만.
    if (!value.unlimited && used === null && target === null) return null
    const pct = target !== null && target > 0 && used !== null ? Math.round((used / target) * 100) : null
    return { off: false, unl: value.unlimited, capa: target, orders: used, pct, risk: null, note: '' }
  }
  // 실 모드(api.capacity 있음)는 첫 조회 전에도 capaOf 를 실어 데모 CAPA 를 숨긴다(null = 표시 없음). capacity 없는 api 는 키를 만들지 않는다.
  // LOGISPRD-14585: 선택 운영일의 AVAILABLE 응답 시각만. 날짜 전환·새로고침은 setCapacity(null) 로 즉시 비워진다.
  const capaUpdatedLabel =
    capacity && capacity.operationDate === capaDate && !capacity.unavailable ? formatCapaUpdatedLabel(capacity.fetchedAt) : CAPA_UPDATED_EMPTY
  const realTargets = {
    // LOGISPRD-16563: 실 모드는 1위 = 전체 센터 자동 할당 고정, 서버 창고는 2위부터(표시만 +1 — 저장 order·ranked 는 창고만).
    ...(snapshot.targets
      ? { targets: snapshot.targets.map((t): [string, string] => [t.label, t.code]), fixedTargets: [] as number[], autoCenters: 'top' as const }
      : {}),
    ...(api.capacity ? { capaOf } : {}),
  }

  const wiring: Wiring = {
    rows: {},
    dispatch: () => ({ state: toDispatch(snapshot), today: new Date(today), nowHour: api.now().getHours(), loading, capaUpdatedLabel, ...realTargets }),
    onDispatchView: (view) => {
      if (view.dayOffset !== dayOffset) {
        requestId.current++
        setDayOffset(view.dayOffset)
      }
    },
    options: { LOADFROMSELECT_OPTIONS: () => snapshot.scenarios.map((s) => ({ value: s.id, label: s.name })) },
    opens: {
      prioBaseModal_apResetAll: (ctx) => {
        if (!ctx.form || ctx.form.tab === 'global' || !ready || loading || writing.current) {
          reportError(new Error('국내 기본 폼의 조회 완료 후 초기화하세요.'))
          return false
        }
        return true
      },
      prioScenModal_apDeleteBtn: (ctx) => {
        if (typeof ctx.form?.id !== 'string' || !ctx.form.id || !ready || loading || writing.current) {
          reportError(new Error('저장된 운영의 조회 완료 후 삭제하세요.'))
          return false
        }
        return true
      },
      prioScenModal_apResetAll: (ctx) => {
        if (typeof ctx.form?.id !== 'string' || !ctx.form.id || !ready || loading || writing.current) {
          reportError(new Error('저장된 운영의 조회 완료 후 초기화하세요.'))
          return false
        }
        return true
      },
    },
    actions: {
      prioToday: async () => {
        requestId.current++
        setToday(operatingDate(api.now()))
        await Promise.resolve()
      },
      prioPrev: async () => {
        await Promise.resolve()
      },
      prioNext: async () => {
        await Promise.resolve()
      },
      prioRefresh: async () => {
        // 실 모드는 CAPA 도 현재 운영일로 다시 조회한다(이전 오류는 조회 시작 시 비운다).
        if (api.capacity) setCapaReload((n) => n + 1)
        await refresh()
      },
      prioBaseModal_ok: perform(async (form) => {
        if (form.tab === 'global') throw new Error('글로벌 순위 UI 입력이 연결되지 않아 저장할 수 없습니다.')
        const hr = hourTarget(form)
        // 편집 전 서버 값(30분 단위가 아닐 수 있다)을 그대로 보내면 단위를 묻지 않는다. 바꾼 값만 30분 단위를 요구한다.
        const order = toPolicyOrder(form.hourOrder as number[], targetCountOf(snapshot))
        const wait = validateWait(form.hourWait, snapshot.base[hr]?.wait)
        // LOGISPRD-14424: 순위 구간 길이 — 편집값, 없으면 서버값. targets 없는 경로는 없다.
        await api.saveBase(hr, withRanked({ order, wait }, rankedOf(form.hourRanked) ?? snapshot.base[hr]?.ranked))
      }),
      prioBaseModal_apResetAll_ok: perform(async (form) => {
        if (form.tab === 'global') throw new Error('글로벌 초기화는 지원하지 않습니다.')
        await api.resetBase()
      }, BASE_DRAFT_RESET),
      prioBaseModal_apPhReset: perform(async (form) => {
        if (form.tab === 'global') throw new Error('글로벌 초기화는 지원하지 않습니다.')
        await api.resetBase(hourTarget(form))
      }, BASE_DRAFT_RESET),
      prioScenModal_ok: perform(async (form, ctx) => {
        // 시간별 편집 전 값(deriveScenario valueAt 과 같은 의미): 초안은 불러온 원본(저장본/원본 운영) 시간 또는
        // 기본 운영 시간(시간 초기화 tombstone 뒤 화면이 보여 주는 값)이다. 저장본은 자기 값(서버 회송값)이다.
        const rawSourceId = form.loadFrom === undefined ? form.id : form.loadFrom
        const rawSaved = form.savedHours as Record<number, Partial<HourPriority> | undefined> | undefined
        const rawSourceHours =
          (rawSourceId === form.id ? rawSaved : undefined) ?? snapshot.scenarios.find((s) => s.id === rawSourceId)?.hours
        const originalWait = (field: 'hourDrafts' | 'savedHours', hour: number): number[] =>
          (field === 'savedHours' ? [rawSaved?.[hour]?.wait] : [rawSourceHours?.[hour]?.wait, snapshot.base[hour]?.wait]).filter(
            (wait): wait is number => typeof wait === 'number',
          )
        const parsed = createScenarioFormSchema(originalWait, targetCountOf(snapshot)).safeParse(form)
        if (!parsed.success) throw new Error('운영명·기간과 시간별 순위·대기시간 입력을 확인하세요.')
        const { id, name, period, loadFrom, hourDrafts, savedHours } = parsed.data
        const sourceId = loadFrom === undefined ? id : loadFrom
        const source = sourceId ? snapshot.scenarios.find((s) => s.id === sourceId) : undefined
        const sourceHours = (sourceId === id ? savedHours : undefined) ?? source?.hours
        if (sourceId && !sourceHours) throw new Error('불러올 운영을 찾을 수 없습니다.')
        const hours = cloneHours(sourceHours ?? {})
        Object.entries(hourDrafts ?? {}).forEach(([hour, draft]) => {
          const hr = Number(hour)
          if (draft === null) delete hours[hr]
          else {
            // 순위는 투영으로, 대기시간은 위 스키마(범위 + 바꾼 값만 30분 단위)로 검증했다.
            // LOGISPRD-14424: ranked 는 초안 값, 없으면 불러온 원본 → 기본 운영 값.
            const ranked = draft.ranked ?? (sourceHours?.[hr] ?? snapshot.base[hr]).ranked
            const value = withRanked({ order: toPolicyOrder(draft.order, targetCountOf(snapshot)), wait: draft.wait }, ranked)
            if (sameRank(value.order, value.ranked, snapshot.base[hr]) && value.wait === snapshot.base[hr].wait) delete hours[hr]
            else hours[hr] = value
          }
        })
        const savedId = await api.saveScenario({ id, name, from: period[0], to: period[1], hours })
        ctx.applyIdentity?.({ id: savedId, savedHours: cloneHours(hours), editing: true, isNew: false, title: '운영 편집' })
      }),
      prioScenModal_apDeleteBtn_ok: perform(async (form) => {
        await api.deleteScenario(idTarget(form))
      }),
      prioScenModal_apResetAll_ok: perform(async (form) => {
        await api.resetScenario(idTarget(form))
      }, SCEN_DRAFT_RESET),
      prioScenModal_apPhReset: (ctx) => {
        if (ctx.isCurrent?.() === false) return false
        const form = requiredForm(ctx.form)
        const hr = hourTarget(form)
        if (!ctx.applyDraft) throw new Error('초안 갱신 setter가 연결되지 않았습니다.')
        ctx.applyDraft({ hourDrafts: { ...(form.hourDrafts as Record<number, HourPriority | null>), [hr]: null } })
      },
      prioHourExcModal_ok: perform(async (form) => {
        // LOGISPRD-15037: one 범위에서 화면값이 기준값(예외를 뺀 유효값)과 같고 그 시간에 자기 예외가 있으면 저장 대신 예외를 삭제한다.
        // 기준값은 서버값이라 30분 단위가 아닐 수 있고 값을 저장하지도 않으므로 순위·대기시간 스키마 검증을 거치지 않는다.
        if (form.scope !== 'after' && Array.isArray(form.order) && typeof form.wait === 'number') {
          const target = exceptionTarget(form)
          const base = effectiveHour(snapshot, target.date, target.hr, false)
          const useDraft = usableDraftOrder(snapshot, form.order)
          const draftOrder = useDraft ? safePolicyOrder(form.order, targetCountOf(snapshot)) : undefined
          const ranked = (useDraft ? rankedOf(form.ranked) : undefined) ?? base.ranked
          if (draftOrder && sameRank(draftOrder, ranked, base) && form.wait === base.wait && snapshot.exceptions[`${target.date}|${target.hr}`]) {
            await api.resetException(target.date, target.hr)
            return
          }
        }
        // 편집 전 값(예외 → 운영 → 기본 순 유효값)과 같은 대기시간은 30분 단위를 요구하지 않는다.
        // LOGISPRD-14596: after 는 대상 시간 전체에 같은 값을 쓰므로, 대상 시간의 유효 대기시간이 모두 같을 때만 그 값을 완화 기준으로 쓴다.
        // 하나라도 다르면 undefined(30분 단위 강제) — 배열로 넘기면 "하나라도 같으면 허용"이 되어 비 30분 값이 다른 시간으로 퍼진다.
        const originalWait = (() => {
          if (!(typeof form.hr === 'number' && Number.isInteger(form.hr) && form.hr >= 0 && form.hr <= 23 && typeof form.dayOffset === 'number'))
            return undefined
          const date = selectedDate(today, form.dayOffset)
          // LOGISPRD-15037: one 에서 화면 대기시간이 기준값(예외를 뺀 유효값)과 같으면 [초기화] 뒤 순위만 고친 저장이므로 그 값을 완화 기준으로 쓴다.
          if (form.scope !== 'after') {
            const baseWait = effectiveHour(snapshot, date, form.hr, false).wait
            if (form.wait === baseWait) return baseWait
          }
          const waits = exceptionScopeHours(form.hr, form.scope === 'after' ? 'after' : 'one').map((hr) => effectiveHour(snapshot, date, hr).wait)
          return waits.every((wait) => wait === waits[0]) ? waits[0] : undefined
        })()
        const parsed = createHourFormSchema(originalWait, targetCountOf(snapshot)).safeParse(form)
        if (!parsed.success) {
          const priorityIssue = parsed.error.issues.find((issue) => issue.code === 'custom' || issue.path[0] === 'wait')
          throw new Error(priorityIssue?.message ?? '시간·날짜·순위·대기시간 입력이 필요합니다.')
        }
        const target = exceptionTarget(form)
        const order = toPolicyOrder(parsed.data.order, targetCountOf(snapshot))
        // LOGISPRD-14424: ranked 는 폼 값, 없으면 편집 전 유효값(예외 → 운영 → 기본).
        const ranked = parsed.data.ranked ?? effectiveHour(snapshot, target.date, target.hr).ranked
        const value = withRanked({ order, wait: parsed.data.wait }, ranked)
        // LOGISPRD-14596: after 는 대상 시간 전체를 요청 1회로 저장한다. one 은 기존 단일 저장 경로를 유지한다.
        if (parsed.data.scope === 'after') await api.saveExceptions(target.date, exceptionScopeHours(target.hr, 'after'), value)
        else await api.saveException(target.date, target.hr, value)
      }),
      // LOGISPRD-15037: 서버를 호출하지 않는다. 화면 초안만 기준값(예외를 뺀 유효값)으로 채우고, 예외 삭제는 [저장] 때 한다.
      prioHourExcModal_hxResetAll: (ctx) => {
        try {
          const form = requiredForm(ctx.form)
          if (!ready || loading) throw new Error('조회 완료 후 다시 시도하세요.')
          if (writing.current) throw new Error('저장이 진행 중입니다.')
          if (!ctx.applyDraft) throw new Error('초안 갱신 setter가 연결되지 않았습니다.')
          const target = exceptionTarget(form)
          if (ctx.isCurrent?.() === false) return Promise.resolve()
          const base = effectiveHour(snapshot, target.date, target.hr, false)
          // ranked 키를 항상 싣는다(undefined 여도) — 병합으로 이전 form.ranked 가 남지 않게 명시 덮어쓴다.
          ctx.applyDraft({ order: viewOrder(snapshot, base.order), wait: base.wait, ranked: base.ranked })
          return Promise.resolve()
        } catch (reason) {
          if (ctx.isCurrent?.() === false) return Promise.resolve()
          return Promise.reject(reportError(reason))
        }
      },
      // 재조회 없이 동기 계산하지만 액션 계약(Promise, 실패는 reject)을 유지한다.
      prioHourExcModal_hxCopyPrev: (ctx) => {
        try {
          const form = requiredForm(ctx.form)
          if (!ready || loading) throw new Error('조회 완료 후 다시 시도하세요.')
          if (writing.current) throw new Error('저장이 진행 중입니다.')
          if (!ctx.applyDraft) throw new Error('초안 갱신 setter가 연결되지 않았습니다.')
          const target = exceptionTarget(form)
          if (isHourLocked(target.date, target.hr, api.now())) throw new Error('지난 시간 또는 당일 외 시간에는 복사할 수 없습니다.')
          const sourceDate = target.hr === 23 ? selectedDate(today, (form.dayOffset as number) - 1) : target.date
          const sourceHr = target.hr === 0 ? 23 : target.hr - 1
          // 재조회하지 않고 현재 화면 snapshot 으로 계산한다. 실 모드 read 는 버전 보관을 교체해 낙관적 잠금을 우회한다 (LLD T2).
          if (ctx.isCurrent?.() === false) return Promise.resolve()
          const source = effectiveHour(snapshot, sourceDate, sourceHr)
          // 복사는 순위만 쓴다. 원본 대기시간(서버 값은 30분 단위가 아닐 수 있다)은 검증 대상이 아니다.
          const targetCount = targetCountOf(snapshot)
          if (targetCount === undefined) validatePriority({ order: source.order, wait: 0 })
          else validateTargetOrder(source.order, targetCount)
          // LOGISPRD-14424: 순위 구간 길이도 그 시간 값으로 복사한다(실 모드만 값이 있다).
          ctx.applyDraft(withRanked({ order: viewOrder(snapshot, source.order) }, source.ranked))
          return Promise.resolve()
        } catch (reason) {
          if (ctx.isCurrent?.() === false) return Promise.resolve()
          return Promise.reject(reportError(reason))
        }
      },
    },
    form: { prioBaseModal: (form) => deriveSettings(form), prioScenModal: deriveScenario, prioHourExcModal: deriveHour },
  }
  return { wiring, error, loading, ready, capaError }
}
